import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Navigation, Square, Satellite, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const KEY = "gps_trip_active";
const R = 6371000;

function haversine(a, b) {
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
}

const load = () => { try { return JSON.parse(localStorage.getItem(KEY)) || null; } catch { return null; } };

export function GpsTripTracker({ onFinish }) {
    const [trip, setTrip] = useState(load);
    const [err, setErr] = useState("");
    const [now, setNow] = useState(Date.now());
    const [finishing, setFinishing] = useState(false);
    const watchRef = useRef(null);
    const tripRef = useRef(trip);
    tripRef.current = trip;

    const supported = typeof navigator !== "undefined" && !!navigator.geolocation;

    const persist = (t) => { tripRef.current = t; setTrip(t); if (t) localStorage.setItem(KEY, JSON.stringify(t)); else localStorage.removeItem(KEY); };

    const onPos = (pos) => {
        const t = tripRef.current;
        if (!t) return;
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        if (accuracy > 60) return;
        const p = { lat, lng, ts: pos.timestamp };
        const last = t.last;
        let meters = t.meters;
        if (last) {
            const d = haversine(last, p);
            const dt = Math.max((p.ts - last.ts) / 1000, 1);
            if (d < 5 || d / dt > 70) return;
            meters += d;
        }
        persist({ ...t, start: t.start || p, last: p, meters, points: (t.points || 0) + 1 });
    };

    const startWatch = () => {
        if (!supported || watchRef.current != null) return;
        watchRef.current = navigator.geolocation.watchPosition(onPos, (e) => setErr(e.code === 1 ? "Povolte přístup k poloze v prohlížeči." : "Poloha není dostupná."),
            { enableHighAccuracy: true, maximumAge: 2000, timeout: 20000 });
    };
    const stopWatch = () => { if (watchRef.current != null) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; } };

    useEffect(() => {
        if (trip) startWatch();
        const iv = setInterval(() => setNow(Date.now()), 1000);
        return () => { stopWatch(); clearInterval(iv); };
    }, []); // eslint-disable-line

    const start = () => {
        if (!supported) { toast.error("Prohlížeč nepodporuje určování polohy"); return; }
        setErr("");
        persist({ startedAt: Date.now(), meters: 0, points: 0, start: null, last: null });
        startWatch();
        toast.success("GPS jízda zahájena — nechte stránku otevřenou");
    };

    const geocode = async (p) => {
        if (!p) return "";
        try { const { data } = await api.get(`/trips/reverse-geocode?lat=${p.lat}&lng=${p.lng}`); return data.address || ""; } catch { return `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`; }
    };

    const finish = async () => {
        const t = tripRef.current;
        if (!t) return;
        stopWatch();
        setFinishing(true);
        const [from, to] = await Promise.all([geocode(t.start), geocode(t.last)]);
        const km = Math.round((t.meters / 1000) * 10) / 10;
        const startDate = new Date(t.startedAt);
        onFinish({
            date: startDate.toISOString().slice(0, 10), from_place: from, to_place: to, km: km || "",
            source: "gps", duration_min: Math.round((Date.now() - t.startedAt) / 60000),
            start_time: startDate.toTimeString().slice(0, 5), end_time: new Date().toTimeString().slice(0, 5),
            start_lat: t.start?.lat ?? null, start_lng: t.start?.lng ?? null, end_lat: t.last?.lat ?? null, end_lng: t.last?.lng ?? null,
            points_count: t.points || 0,
        });
        persist(null);
        setFinishing(false);
        if (!km) toast.warning("Ujeto méně než 100 m — zadejte km ručně");
    };

    const cancel = () => { if (!window.confirm("Zrušit probíhající GPS jízdu bez uložení?")) return; stopWatch(); persist(null); };

    if (!trip) {
        return (
            <button onClick={start} data-testid="gps-start" disabled={!supported} title={supported ? "" : "Prohlížeč nepodporuje GPS"}
                    className="inline-flex items-center gap-2 border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white disabled:opacity-40 px-4 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors">
                <Navigation className="w-4 h-4" /> Start GPS jízdy
            </button>
        );
    }

    const secs = Math.max(0, Math.floor((now - trip.startedAt) / 1000));
    const hh = String(Math.floor(secs / 3600)).padStart(2, "0"), mm = String(Math.floor((secs % 3600) / 60)).padStart(2, "0"), ss = String(secs % 60).padStart(2, "0");
    const km = (trip.meters / 1000).toFixed(1);

    return (
        <div className="w-full bg-slate-900 text-white border-2 border-amber-500 p-4 flex flex-wrap items-center gap-6" data-testid="gps-panel">
            <div className="flex items-center gap-2 text-amber-400">
                <Satellite className="w-5 h-5 animate-pulse" />
                <span className="text-[10px] uppercase tracking-widest font-semibold">GPS jízda probíhá</span>
            </div>
            <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Ujeto</div><div className="font-display text-2xl font-black tabular-data" data-testid="gps-km">{km} km</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Čas</div><div className="font-display text-2xl font-black tabular-data">{hh}:{mm}:{ss}</div></div>
            <div className="text-xs text-slate-400">{trip.points} bodů{trip.last ? "" : " · čekám na signál…"}</div>
            {err && <div className="flex items-center gap-1.5 text-xs text-red-300"><AlertTriangle className="w-3.5 h-3.5" /> {err}</div>}
            <div className="flex-1"></div>
            <button onClick={cancel} className="px-3 py-2 text-xs uppercase tracking-wider border border-slate-600 text-slate-300 hover:bg-slate-800 rounded-sm">Zrušit</button>
            <button onClick={finish} disabled={finishing} data-testid="gps-stop"
                    className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-slate-900 px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm">
                <Square className="w-3.5 h-3.5" /> {finishing ? "Zjišťuji adresy…" : "Ukončit a uložit"}
            </button>
        </div>
    );
}
